/* eslint-disable no-await-in-loop */
/* eslint-disable no-use-before-define */
import { useCallback, useEffect, useState } from 'react';
import { useNotifications } from '../../../../../hooks/useNotifications';
import { CustomListsService } from '../../../../../services/customListService';

export const useListNodesContextValue = (listId) => {
  const [nodesData, setNodesData] = useState({
    nodes: [],
    total: 0,
    pageSize: 0,
    page: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { showErrorNotification } = useNotifications();
  const [dndState, setDndState] = useState({
    isDragging: true,
    draggedNode: null,
    anchorNode: null,
    targetPath: null,
    targetIndex: null,
  });

  const updateNodesData = useCallback((newData) => {
    setNodesData((current) => ({ ...current, ...newData }));
  }, []);

  const updateNodeRecursive = useCallback(
    ({ nodeId, newData, currentNodes = [], path, pathIndex = 0, updateHandler }) => {
      const newNodes = [];
      currentNodes.forEach((node) => {
        if (pathIndex >= path.length && nodeId === node.id) {
          newNodes.push(
            updateHandler
              ? updateHandler(node)
              : {
                ...node,
                ...newData,
              },
          );
        } else if (path[pathIndex] === node.id) {
          newNodes.push({
            ...node,
            nodes: updateNodeRecursive({
              nodeId,
              newData,
              currentNodes: node.nodes,
              path,
              pathIndex: pathIndex + 1,
              updateHandler,
            }),
          });
        } else {
          newNodes.push(node);
        }
      });
      return newNodes;
    },
    [],
  );

  const getPages = useCallback(async (path, page, search = '', pageSize = 200) => {
    let currentPage = 1;
    const resultNodes = [];
    const verifyNodes = (newNodes) => {
      const nodesMap = resultNodes.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
      for (const node of newNodes) {
        if (nodesMap[node.id]) continue;
        resultNodes.push(node);
      }
    };
    let total = 0;
    while (currentPage <= page) {
      // eslint-disable-next-line no-await-in-loop
      const result = await CustomListsService.getListItems(listId, path, currentPage, search, pageSize);
      total = result.total;
      pageSize = result.pageSize;
      verifyNodes(result.listItems);
      currentPage += 1;
    }
    return {
      total,
      pageSize,
      nodes: resultNodes,
    };
  }, []);

  const getListNodesPaginated = useCallback(
    async ({ nodeId, path = [], page = 1 } = {}) => {
      if (!listId) {
        updateNodesData({ nodes: [] });
        return;
      }
      setIsLoading(true);
      try {
        const nodePath = [...path];
        if (nodeId) {
          nodePath.push(nodeId);
        }
        const pagesResult = await getPages(nodePath, page);
        if (!nodeId) {
          updateNodesData({
            nodes: pagesResult.nodes,
            total: pagesResult.total,
            pageSize: pagesResult.pageSize,
            page,
          });
        } else {
          setNodesData((currentNodes) => {
            const newNodes = updateNodeRecursive({
              nodeId,
              path,
              updateHandler: (node) => ({
                ...node,
                hasChildren: pagesResult.total > 0,
                nodes: pagesResult.nodes,
                total: pagesResult.total,
                page,
                pageSize: pagesResult.pageSize,
              }),
              currentNodes: currentNodes.nodes,
            });
            return { ...currentNodes, nodes: newNodes };
          });
        }
      } catch (error) {
        showErrorNotification({
          title: 'error',
          description: 'errorRetrievingRecords',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [listId, showErrorNotification, nodesData.nodes],
  );

  const getListNodes = useCallback(async () => {
    if (!listId) {
      updateNodesData({ nodes: [] });
      return;
    }
    setIsLoading(true);
    try {
      await getListNodesPaginated();
    } catch (error) {
      showErrorNotification({
        title: 'error',
        description: 'errorRetrievingRecords',
      });
    } finally {
      setIsLoading(false);
    }
  }, [listId, showErrorNotification]);

  const updateListNodes = useCallback(
    async (newNodes) => {
      setIsLoading(true);
      try {
        const data = await CustomListsService.setListNodes(listId, newNodes);
        updateNodesData({ nodes: data.listNodes });
      } catch (error) {
        showErrorNotification({
          title: 'error',
          description: 'errorUpdatingRecord',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [listId, showErrorNotification],
  );

  const removeNodeRecursive = useCallback((nodeId, currentNodes = [], path, pathIndex = 0) => {
    let removedNode = null;
    const newNodes = [];
    currentNodes.forEach((node) => {
      if (pathIndex >= path.length && nodeId === node.id) {
        removedNode = node;
      } else if (path[pathIndex] === node.id) {
        const result = removeNodeRecursive(nodeId, node.nodes, path, pathIndex + 1);
        removedNode = result.removedNode;
        newNodes.push({
          ...node,
          nodes: result.newNodes,
        });
      } else {
        newNodes.push(node);
      }
    });
    return {
      removedNode,
      newNodes,
    };
  }, []);

  const addNodeRecursive = useCallback((newNode, currentNodes, path, newIndex, pathIndex = 0) => {
    currentNodes ??= [];
    if (pathIndex >= path.length) {
      const newNodes = [...currentNodes];
      newNodes.splice(newIndex ?? newNodes.length, 0, newNode);
      return newNodes;
    }
    const newNodes = [];
    currentNodes.forEach((node) => {
      if (path[pathIndex] === node.id) {
        const nextPathLevel = pathIndex + 1;
        if (nextPathLevel >= path.length && node.total >= node.page * node.pageSize) {
          newNodes.push({ ...node, total: node.total + 1, hasChildren: true });
          return;
        }
        const resultNodes = addNodeRecursive(newNode, node.nodes, path, newIndex, nextPathLevel);
        newNodes.push({
          ...node,
          nodes: resultNodes,
          hasChildren: resultNodes?.length > 0,
        });
      } else {
        newNodes.push(node);
      }
    });
    return newNodes;
  }, []);

  const moveNode = useCallback((currentNodes, nodeId, currentPath, newPath, newIndex) => {
    const { removedNode, newNodes } = removeNodeRecursive(nodeId, currentNodes, currentPath);
    return addNodeRecursive(removedNode, newNodes, newPath, newIndex);
  }, []);

  const moveListNode = useCallback(
    async ({ currentPath, nodeId, newPath, newIndex }) => {
      setIsLoading(true);
      try {
        await CustomListsService.moveListItem({
          listId,
          nodeId,
          path: currentPath,
          newPath,
          newIndex,
        });
        const newNodes = moveNode(nodesData.nodes, nodeId, currentPath, newPath, newIndex);
        updateNodesData({ nodes: newNodes });
      } catch (error) {
        showErrorNotification({
          title: 'error',
          description: 'errorUpdatingRecord',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [nodesData.nodes],
  );

  const addListNode = useCallback(
    async ({ path, nodeData }) => {
      setIsLoading(true);
      try {
        const { node } = await CustomListsService.addListItem({
          listId,
          nodeData,
          path,
        });
        nodeData.id = node.id;
        const newNodes = addNodeRecursive(nodeData, nodesData.nodes, path);
        updateNodesData({ nodes: newNodes });
      } catch (error) {
        showErrorNotification({
          title: 'error',
          description: 'errorCreatingRecord',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [nodesData.nodes],
  );

  const deleteListNode = useCallback(
    async ({ path, nodeId }) => {
      setIsLoading(true);
      try {
        await CustomListsService.deleteListItem({
          listId,
          nodeId,
          path,
        });
        const { newNodes } = removeNodeRecursive(nodeId, nodesData.nodes, path);
        updateNodesData({ nodes: newNodes });
      } catch (error) {
        showErrorNotification({
          title: 'error',
          description: 'errorRemovingRecord',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [nodesData.nodes],
  );

  const updateListNode = useCallback(
    async ({ path, nodeId, nodeData }) => {
      setIsLoading(true);
      try {
        await CustomListsService.updateListItem({
          listId,
          nodeId,
          nodeData,
          path,
        });
        const newNodes = updateNodeRecursive({ nodeId, newData: nodeData, currentNodes: nodesData.nodes, path });
        updateNodesData({ nodes: newNodes });
      } catch (error) {
        showErrorNotification({
          title: 'error',
          description: 'errorUpdatingRecord',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [nodesData.nodes],
  );

  useEffect(() => {
    getListNodes();
  }, [getListNodes]);

  const startDragging = useCallback(({ draggedNode } = {}) => {
    setDndState({ draggedNode, isDragging: true });
  }, []);

  const updateDropTarget = useCallback(({ anchorNode, targetPath, targetIndex }) => {
    setDndState((current) => ({ ...current, anchorNode, targetPath, targetIndex }));
  }, []);

  const stopDragging = useCallback(() => {
    if (dndState.targetIndex != null) {
      const isSamePath =
        dndState.draggedNode.path.length === dndState.targetPath.length &&
        dndState.draggedNode.path.every((item, index) => dndState.targetPath.indexOf(item) === index);
      let newIndex = dndState.targetIndex;
      if (isSamePath && dndState.targetIndex > dndState.draggedNode.nodeIndex) {
        newIndex = dndState.targetIndex - 1;
      }
      moveListNode({
        currentPath: dndState.draggedNode.path,
        nodeId: dndState.draggedNode.id,
        newPath: dndState.targetPath,
        newIndex,
      });
    }
    setDndState({ isDragging: false });
  }, [moveListNode, dndState]);

  const updateNodeData = useCallback(
    ({ nodeId, nodeData, path }) => {
      const newNodes = updateNodeRecursive({ nodeId, newData: nodeData, currentNodes: nodesData.nodes, path });
      updateNodesData({ nodes: newNodes });
    },
    [nodesData.nodes],
  );

  return {
    nodes: nodesData.nodes,
    total: nodesData.total,
    page: nodesData.page,
    pageSize: nodesData.pageSize,
    setListNodes: updateListNodes,
    getListNodes,
    isLoading,
    updateListNode,
    addListNode,
    deleteListNode,
    moveListNode,
    getListNodesPaginated,
    updateNodeData,
    dnd: {
      ...dndState,
      startDragging,
      stopDragging,
      updateDropTarget,
    },
  };
};
